import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, SunIcon, MoonIcon, ChevronDownIcon } from 'lucide-react';
interface NavbarProps {
  isDark: boolean;
  toggleTheme: () => void;
}
export function Navbar({
  isDark,
  toggleTheme
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Services',
    href: '/#services'
  }, {
    name: 'About',
    href: '/#about'
  }, {
    name: 'Contact',
    href: '/#contact'
  }];
  const toolLinks = [{
    name: 'Portfolio Pulse Check',
    href: '/tools'
  }, {
    name: 'Loan Calculator',
    href: '/tools'
  }, {
    name: 'Savings Goal',
    href: '/tools'
  }, {
    name: 'Budget Planner',
    href: '/tools'
  }, {
    name: 'Investment Growth',
    href: '/tools'
  }];
  // Close dropdown when route changes
  useEffect(() => {
    setIsToolsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 dark:bg-[#0f1629]/90 backdrop-blur-md border-b border-slate-200 dark:border-white/10 py-3 shadow-sm dark:shadow-none' : 'bg-transparent py-5'}`} data-id="element-264">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-265">
        <div className="flex justify-between items-center" data-id="element-266">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-id="element-267">
            <img src={isDark ? 'https://cdn.magicpatterns.com/uploads/ijbn574AJz6iw1qrM49TjL/Blue_and_Black_Minimalist_Brand_Logo_-_4.png' : 'https://cdn.magicpatterns.com/uploads/5vDwqvkMZA2x7h9wkDud7B/Blue_and_Black_Minimalist_Brand_Logo_-_2.png'} alt="Scale Finance Strategies" className="h-9 w-9 rounded-lg object-contain" data-id="element-268" />
            <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-white" data-id="element-269">
              Scale
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-270">
                FinanceStrategies
              </span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-271">
            <Link to="/#services" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-272">
              Services
            </Link>

            {/* Tools Dropdown */}
            <div className="relative" onMouseEnter={() => setIsToolsDropdownOpen(true)} onMouseLeave={() => setIsToolsDropdownOpen(false)} data-id="element-273">
              <Link to="/tools" className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors py-2" data-id="element-274">
                Tools
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isToolsDropdownOpen ? 'rotate-180' : ''}`} data-id="element-275" />
              </Link>

              {isToolsDropdownOpen && <div className="absolute top-full left-1/2 -translate-x-1/2 w-56 pt-2" data-id="element-276">
                  <div className="bg-white dark:bg-[#1a2035] rounded-xl shadow-xl border border-slate-200 dark:border-white/10 overflow-hidden py-2" data-id="element-277">
                    {toolLinks.map((link, idx) => <Link key={idx} to={link.href} className="block px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-278">
                        {link.name}
                      </Link>)}
                  </div>
                </div>}
            </div>

            <Link to="/#about" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-279">
              About
            </Link>

            <Link to="/#contact" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-280">
              Contact
            </Link>

            <div className="flex items-center gap-3 pl-6 border-l border-slate-200 dark:border-white/10" data-id="element-281">
              <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-282">
                {isDark ? <SunIcon className="w-5 h-5" data-id="element-283" /> : <MoonIcon className="w-5 h-5" data-id="element-284" />}
              </button>
              <Link to="/#contact" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-accent-500 to-primary-500 text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02]" data-id="element-285">
                Book a Strategy Session
              </Link>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center gap-3" data-id="element-286">
            <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-287">
              {isDark ? <SunIcon className="w-5 h-5" data-id="element-288" /> : <MoonIcon className="w-5 h-5" data-id="element-289" />}
            </button>
            <button className="p-2 text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-290">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-291" /> : <MenuIcon className="w-6 h-6" data-id="element-292" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-[#0f1629]/95 backdrop-blur-xl border-b border-slate-200 dark:border-white/10 py-4 px-4 flex flex-col gap-2 shadow-xl max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-293">
          <Link to="/#services" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-294">
            Services
          </Link>

          <div className="flex flex-col" data-id="element-295">
            <button onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)} className="flex items-center justify-between text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" data-id="element-296">
              Tools
              <ChevronDownIcon className={`w-5 h-5 transition-transform ${isToolsDropdownOpen ? 'rotate-180' : ''}`} data-id="element-297" />
            </button>

            {isToolsDropdownOpen && <div className="pl-6 pr-3 py-2 flex flex-col gap-1 border-l-2 border-slate-100 dark:border-white/5 ml-3" data-id="element-298">
                {toolLinks.map((link, idx) => <Link key={idx} to={link.href} className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-299">
                    {link.name}
                  </Link>)}
              </div>}
          </div>

          <Link to="/#about" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-300">
            About
          </Link>

          <Link to="/#contact" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-301">
            Contact
          </Link>

          <Link to="/#contact" className="mt-4 text-center px-5 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold shadow-md" onClick={() => setIsMobileMenuOpen(false)} data-id="element-302">
            Book a Strategy Session
          </Link>
        </div>}
    </nav>;
}